import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Activity, BarChart3 } from 'lucide-react';
import { SessionsList } from '../components/admin/SessionsList';
import { SessionFeedback } from '../components/admin/SessionFeedback';
import { SessionMessages } from '../components/admin/SessionMessages';
import { AdminSession } from '../types/admin';

const Analyze: React.FC = () => {
  const { user } = useAuth();
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(null);

  if (!user || !user.roles?.includes('sessionsReader')) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="rounded-full bg-red-100 p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access session analysis.
          </p>
        </div>
      </div>
    );
  }

  const handleSessionSelect = (session: AdminSession) => {
    setSelectedSession(session);
  };

  return (
    <div className="h-full flex">
      <div className="w-80 border-r border-gray-200 flex-shrink-0">
        <SessionsList 
          onSessionSelect={handleSessionSelect}
          selectedSessionId={selectedSession?.id}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Activity className="w-8 h-8 mr-3 text-purple-600" />
              Session Analysis
            </h1>
            <p className="text-gray-600 mt-2">
              Analyze user sessions, conversation patterns, and engagement metrics.
            </p>
          </div>

          {selectedSession ? (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Session Information</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Title</h3>
                    <p className="text-gray-900">{selectedSession.title}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">User ID</h3>
                    <p className="text-gray-900 font-mono text-sm">{selectedSession.userId}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Session Type</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedSession.sessionType === 'foundry' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedSession.sessionType}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Messages</h3>
                    <p className="text-gray-900">{selectedSession.messageCount}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Created</h3>
                    <p className="text-gray-900">{new Date(selectedSession.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                    <p className="text-gray-900">{new Date(selectedSession.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <SessionFeedback sessionId={selectedSession.id} />

              <SessionMessages sessionId={selectedSession.id} />
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Session</h3>
              <p className="text-gray-600">
                Choose a session from the left panel to view detailed analysis and feedback.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analyze;
